<template>
  <div>
    <div class="mt-3">
      <v-row dense>
        <v-col cols="12">
          <v-autocomplete autocomplete="off"
            :hide-details="hideDetails"
            variant="outlined"
            :density="density"
            :items="documentType"
            :model-value="passport.documentType"
            item-title="name"
            item-value="code"
            :rules="requiredRules"
            @update:model-value="updateField('documentType', $event)"
          >
            <template #label>
              Тип документа <span v-if="required" class="passport-required">*</span>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field autocomplete="off"
            :hide-details="hideDetails"
            :model-value="passport.series"
            variant="outlined"
            :density="density"
            :maxlength="isRussianPassport ? 4 : undefined"
            :inputmode="isRussianPassport ? 'numeric' : undefined"
            @update:model-value="updateSeries"
          >
            <template #label>
              Серия
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field autocomplete="off"
            :hide-details="hideDetails"
            :model-value="passport.number"
            variant="outlined"
            :density="density"
            :rules="requiredRules"
            @update:model-value="updateField('number', $event)"
          >
            <template #label>
              Номер <span v-if="required" class="passport-required">*</span>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field autocomplete="off"
            class="passport-date-field"
            type="date"
            :hide-details="hideDetails"
            variant="outlined"
            :model-value="passport.issueDate"
            :density="density"
            :min="issueDateMin"
            :max="issueDateMax"
            :rules="issueDateRules"
            @update:model-value="updateField('issueDate', $event)"
          >
            <template #label>
              Дата выдачи <span v-if="required" class="passport-required">*</span>
            </template>
          </v-text-field>
        </v-col>

        <v-col v-if="isRussianPassport" cols="12" md="6">
          <v-text-field autocomplete="off"
            placeholder="000-000"
            maxlength="7"
            :hide-details="hideDetails"
            variant="outlined"
            :model-value="passport.subdivisionCode"
            :density="density"
            :rules="subdivisionCodeRules"
            @update:model-value="updateSubdivisionCode"
          >
            <template #label>
              Код подразделения <span v-if="required" class="passport-required">*</span>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12">
          <v-text-field autocomplete="off"
            variant="outlined"
            :density="density"
            auto-grow
            rows="2"
            :hide-details="hideDetails"
            :model-value="passport.issuedBy"
            :rules="requiredRules"
            @update:model-value="updateField('issuedBy', $event)"
          >
            <template #label>
              Кем выдан <span v-if="required" class="passport-required">*</span>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
/**
 * @typedef {Object} PassportData
 * @property {string|null} documentType
 * @property {string} series
 * @property {string} number
 * @property {string} issueDate YYYY-MM-DD
 * @property {string} subdivisionCode ddd-ddd
 * @property {string} issuedBy
 */
const defaultPassport = () => ({
  documentType: null,
  series: "",
  number: "",
  issueDate: "",
  subdivisionCode: "",
  issuedBy: "",
});

let documentTypeCache = null;
let documentTypeLoadPromise = null;

export default {
  name: "PassportFields",

  emits: ["update:modelValue"],

  props: {
    modelValue: {
      type: Object,
      default: () => defaultPassport(),
    },
    density: {
      type: String,
      default: "compact",
    },
    hideDetails: {
      type: [Boolean, String],
      default: true,
    },
    required: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      documentType: [],
    };
  },

  computed: {
    passport() {
      return {
        ...defaultPassport(),
        ...(this.modelValue || {}),
      };
    },
    //не старше 100 лет и не позже текущей даты
    issueDateMin() {
      return this.formatDateForInput(this.addYears(new Date(), -100));
    },
    issueDateMax() {
      return this.formatDateForInput(new Date());
    },
    requiredRules() {
      return this.required
        ? [(value) => Boolean(value) || "Заполните поле"]
        : [];
    },
    issueDateRules() {
      return [
        ...this.requiredRules,
        (value) => !value || value >= this.issueDateMin || "Дата выдачи не должна быть старше 100 лет",
        (value) => !value || value <= this.issueDateMax || "Дата выдачи не может быть позже текущей даты",
      ];
    },
    subdivisionCodeRules() {
      return [
        ...this.requiredRules,
        (value) => !value || /^\d{3}-\d{3}$/.test(value) || "Введите код в формате 000-000",
      ];
    },
    selectedDocumentType() {
      return this.documentType.find(
        (item) => String(item.code) === String(this.passport.documentType)
      );
    },
    isRussianPassport() {
      const selectedName = this.selectedDocumentType
        ? String(this.selectedDocumentType.name || "").toLowerCase()
        : "";

      return (
        String(this.passport.documentType) === "21" ||
        selectedName.includes("паспорт гражданина рф")
      );
    },
  },

  watch: {
    isRussianPassport(value) {
      if (!value && this.passport.subdivisionCode) {
        this.updateField("subdivisionCode", "");
      }

      if (value && this.passport.series) {
        this.updateField("series", this.formatRussianPassportSeries(this.passport.series));
      }
    },
  },

  created() {
    this.loadDirectory();
  },

  methods: {
    addYears(date, years) {
      const result = new Date(date);
      result.setFullYear(result.getFullYear() + years);

      return result;
    },
    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    formatSubdivisionCode(value) {
      const digits = String(value || "").replace(/\D/g, "").slice(0, 6);

      if (digits.length <= 3) {
        return digits;
      }

      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    },
    formatRussianPassportSeries(value) {
      return String(value || "").replace(/\D/g, "").slice(0, 4);
    },
    updateField(field, value) {
      this.$emit("update:modelValue", {
        ...this.passport,
        [field]: value,
      });
    },
    updateSeries(value) {
      this.updateField(
        "series",
        this.isRussianPassport ? this.formatRussianPassportSeries(value) : value
      );
    },
    updateSubdivisionCode(value) {
      this.updateField("subdivisionCode", this.formatSubdivisionCode(value));
    },
    loadDirectory() {
      if (documentTypeCache) {
        this.documentType = [...documentTypeCache];
        return;
      }

      if (!documentTypeLoadPromise) {
        documentTypeLoadPromise = this.axios
          .post(
            this.$model.wwwroot +
              "/procollege/core_procollege/helpers/DirectoryLoader/Load",
            new URLSearchParams({
              directory: JSON.stringify(["documentType"]),
            })
          )
          .then((res) => {
            documentTypeCache = Object.values(res.data.documentType || {}).map(
              (item) => ({
                code: item.code,
                name: item.name,
              })
            );

            return documentTypeCache;
          })
          .catch((error) => {
            documentTypeLoadPromise = null;
            throw error;
          });
      }

      documentTypeLoadPromise.then((documentType) => {
        this.documentType = [...documentType];
      });
    },
  },
};
</script>

<style scoped>
.v-text-field {
  margin-bottom: 8px;
}

.passport-required {
  color: #d92d20;
  font-weight: 700;
}

.passport-date-field :deep(input[type="date"]) {
  position: relative;
  padding-right: 44px;
}

.passport-date-field :deep(input[type="date"]::-webkit-calendar-picker-indicator) {
  position: absolute;
  right: 10px;
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
}
</style>
